import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  Area,
  ComposedChart,
  Bar,
} from 'recharts';
import { DashboardTrends, TrendDataPoint } from '../../types/dashboard.types';

interface DashboardTrendChartProps {
  trends: DashboardTrends;
  periodDays: number;
  onPeriodChange: (days: number) => void;
}

/**
 * Period selector options.
 */
const PERIOD_OPTIONS = [
  { value: 7, label: '7d' },
  { value: 30, label: '30d' },
  { value: 90, label: '90d' },
];

/**
 * Format date for chart display.
 */
function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * Custom tooltip for the chart.
 */
const CustomTooltip: React.FC<{
  active?: boolean;
  payload?: Array<{ name: string; value: number | null; color: string }>;
  label?: string;
}> = ({ active, payload, label }) => {
  const { t } = useTranslation();

  if (!active || !payload || !label) {
    return null;
  }

  return (
    <div className="bg-white p-3 shadow-lg rounded-lg border border-gray-200">
      <p className="text-sm font-medium text-gray-900 mb-2">{formatDate(label)}</p>
      {payload.map((entry, index) => (
        <p key={index} className="text-sm" style={{ color: entry.color }}>
          {entry.name === 'call_count'
            ? t('dashboard:trends.calls')
            : t('dashboard:trends.complianceRate')}
          : {entry.value !== null ? (entry.name === 'call_count' ? entry.value : `${entry.value}%`) : '-'}
        </p>
      ))}
    </div>
  );
};

/**
 * Trend chart showing call volume and compliance rate over time.
 */
const DashboardTrendChart: React.FC<DashboardTrendChartProps> = ({
  trends,
  periodDays,
  onPeriodChange,
}) => {
  const { t } = useTranslation();

  // Transform data for chart - ensure compliance_pass_rate is number or null
  const chartData = trends.data_points.map((point: TrendDataPoint) => ({
    date: point.date,
    call_count: point.call_count,
    compliance_pass_rate: point.compliance_pass_rate,
  }));

  // Check if there's any data to show
  const hasData = chartData.some(d => d.call_count > 0);

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-medium text-gray-900">
          {t('dashboard:trends.title')}
        </h3>
        <div className="flex rounded-md shadow-sm">
          {PERIOD_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => onPeriodChange(option.value)}
              className={`
                relative inline-flex items-center px-3 py-2 text-sm font-medium
                ${option.value === periodDays
                  ? 'bg-indigo-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
                }
                ${option.value === 7 ? 'rounded-l-md' : ''}
                ${option.value === 90 ? 'rounded-r-md' : ''}
                border border-gray-300 focus:z-10 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500
              `}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {!hasData ? (
        <div className="h-64 flex items-center justify-center text-gray-500">
          <p>{t('dashboard:trends.noData')}</p>
        </div>
      ) : (
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis
                dataKey="date"
                tickFormatter={formatDate}
                tick={{ fontSize: 12, fill: '#6B7280' }}
                tickLine={false}
                axisLine={{ stroke: '#E5E7EB' }}
                interval="preserveStartEnd"
              />
              <YAxis
                yAxisId="left"
                tick={{ fontSize: 12, fill: '#6B7280' }}
                tickLine={false}
                axisLine={{ stroke: '#E5E7EB' }}
                width={40}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                domain={[0, 100]}
                tick={{ fontSize: 12, fill: '#6B7280' }}
                tickLine={false}
                axisLine={{ stroke: '#E5E7EB' }}
                tickFormatter={(value: number) => `${value}%`}
                width={50}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                formatter={(value: string) =>
                  value === 'call_count'
                    ? t('dashboard:trends.calls')
                    : t('dashboard:trends.complianceRate')
                }
              />
              <Bar
                yAxisId="left"
                dataKey="call_count"
                fill="#818CF8"
                fillOpacity={0.6}
                name="call_count"
                radius={[2, 2, 0, 0]}
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="compliance_pass_rate"
                stroke="#10B981"
                strokeWidth={2}
                dot={{ fill: '#10B981', strokeWidth: 0, r: 3 }}
                activeDot={{ r: 5, strokeWidth: 0 }}
                name="compliance_pass_rate"
                connectNulls
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

export default DashboardTrendChart;
